import { Alert, Box, Button, Drawer, Fab, IconButton, TextField, Theme, Tooltip, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import React, { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { v4 as uuid4 } from 'uuid';
import { EZPAY_API_URL } from '../util/Constants';
import {
  assistantMessageMaxLength,
  AssistantError,
  AssistantErrorKind,
  getAssistantChatUrl,
  streamAssistantChat,
} from '../util/MerchantAssistant';
import { getStandardHeaders } from '../util/StandardRequestHeaders';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    launcher: {
      position: 'fixed',
      right: theme.spacing(3),
      bottom: theme.spacing(3),
      zIndex: theme.zIndex.drawer - 1,
    },
    drawerPaper: {
      width: 440,
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1, 1, 1, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    note: {
      padding: theme.spacing(1, 2),
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    messages: {
      flex: 1,
      overflowY: 'auto',
      padding: theme.spacing(2),
    },
    userMessage: {
      marginLeft: theme.spacing(6),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1, 1.5),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.grey[100],
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    assistantMessage: {
      marginBottom: theme.spacing(2),
      wordBreak: 'break-word',
      '& p': {
        margin: theme.spacing(0, 0, 1, 0),
      },
      '& table': {
        borderCollapse: 'collapse',
        margin: theme.spacing(1, 0),
        fontSize: '0.875rem',
        display: 'block',
        overflowX: 'auto',
      },
      '& th, & td': {
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0.5, 1),
        textAlign: 'left',
      },
      '& th': {
        backgroundColor: theme.palette.grey[100],
      },
      '& pre': {
        overflowX: 'auto',
      },
    },
    composer: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: theme.spacing(1),
      padding: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  }),
);

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  // Assistant messages only: waiting for the first token, receiving, or finished.
  status?: 'thinking' | 'streaming' | 'done';
  error?: AssistantError;
}

export interface MerchantAssistantProps {
  tenantId: string;
  tenantName: string;
  // Overridable for tests; the portal uses the configured API host and the standard request headers.
  chatUrl?: string;
  getHeaders?: () => Promise<Record<string, string>>;
}

const defaultGetHeaders = async (): Promise<Record<string, string>> => ({ ...(await getStandardHeaders()) });

const MerchantAssistant: React.FC<MerchantAssistantProps> = props => {
  const { tenantId, tenantName, chatUrl = getAssistantChatUrl(EZPAY_API_URL), getHeaders = defaultGetHeaders } = props;
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState(() => uuid4());
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isInFlight, setIsInFlight] = useState(false);
  // Mirrors isInFlight synchronously, so two Enter presses before a re-render cannot start two turns.
  const inFlightRef = useRef(false);
  const abortRef = useRef<AbortController>();
  const listEndRef = useRef<HTMLDivElement>(null);

  const endTurn = () => {
    inFlightRef.current = false;
    setIsInFlight(false);
  };

  const resetConversation = () => {
    abortRef.current?.abort();
    abortRef.current = undefined;
    setConversationId(uuid4());
    setMessages([]);
    endTurn();
  };

  // A conversation belongs to one tenant: start over whenever the admin switches tenant.
  const conversationTenantId = useRef(tenantId);
  useEffect(() => {
    if (conversationTenantId.current !== tenantId) {
      conversationTenantId.current = tenantId;
      resetConversation();
    }
  }, [tenantId]);

  // Stop any stream still running when the panel unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    listEndRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages]);

  const updateMessage = (id: string, update: (message: ChatMessage) => ChatMessage) => {
    setMessages(prev => prev.map(m => (m.id === id ? update(m) : m)));
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || text.length > assistantMessageMaxLength || inFlightRef.current) return;
    inFlightRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;
    const answerId = uuid4();
    setMessages(prev => [
      ...prev,
      { id: uuid4(), role: 'user', text },
      { id: answerId, role: 'assistant', text: '', status: 'thinking' },
    ]);
    setInput('');
    setIsInFlight(true);

    // Every callback checks the controller so a reset or a tenant switch silences a stream still in flight.
    const isCurrent = () => !controller.signal.aborted;
    let headers: Record<string, string>;
    try {
      headers = await getHeaders();
    } catch (e) {
      if (!isCurrent()) return;
      updateMessage(answerId, m => ({
        ...m,
        status: 'done',
        error: {
          kind: AssistantErrorKind.SessionExpired,
          message: 'Your session has expired. Sign in again to keep using the assistant.',
        },
      }));
      endTurn();
      return;
    }
    if (!isCurrent()) return;
    await streamAssistantChat(
      { url: chatUrl, headers, message: text, conversationId, signal: controller.signal },
      {
        onToken: token => {
          if (isCurrent()) updateMessage(answerId, m => ({ ...m, status: 'streaming', text: m.text + token }));
        },
        // The final answer replaces whatever was streamed.
        onMessage: full => {
          if (isCurrent()) updateMessage(answerId, m => ({ ...m, status: 'streaming', text: full }));
        },
        onError: error => {
          if (isCurrent()) updateMessage(answerId, m => ({ ...m, error }));
        },
        onDone: () => {
          if (!isCurrent()) return;
          updateMessage(answerId, m => ({
            ...m,
            status: 'done',
            error:
              m.error ??
              (m.text ? undefined : { kind: AssistantErrorKind.Stream, message: 'The assistant ended the turn without an answer.' }),
          }));
          endTurn();
        },
      },
    );
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Enter sends; Shift+Enter starts a new line.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderAssistantMessage = (message: ChatMessage) => (
    <Box key={message.id} className={classes.assistantMessage}>
      {message.status === 'thinking' && !message.error && <Typography color="text.secondary">Thinking…</Typography>}
      {message.text && (
        // skipHtml drops any raw HTML in the answer; react-markdown never renders it via innerHTML.
        <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml linkTarget="_blank">
          {message.text}
        </ReactMarkdown>
      )}
      {message.error && (
        <Alert
          severity="error"
          action={
            message.error.kind === AssistantErrorKind.SessionExpired ? (
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                Sign in again
              </Button>
            ) : undefined
          }
        >
          {message.error.message}
        </Alert>
      )}
    </Box>
  );

  const canSend = !isInFlight && input.trim().length > 0;

  return (
    <>
      {!isOpen && (
        <Tooltip title="Merchant Assistant">
          <Fab color="primary" className={classes.launcher} onClick={() => setIsOpen(true)} aria-label="Open Merchant Assistant">
            <ChatOutlinedIcon />
          </Fab>
        </Tooltip>
      )}
      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} classes={{ paper: classes.drawerPaper }}>
        <Box className={classes.header}>
          <Typography variant="subtitle" component="h2">
            Merchant Assistant
          </Typography>
          <Box>
            <Button size="small" onClick={resetConversation} data-cy="btn-assistant-new-conversation">
              New conversation
            </Button>
            <IconButton onClick={() => setIsOpen(false)} aria-label="Close Merchant Assistant">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Typography variant="body2" className={classes.note}>
          {`Read-only assistant · answers about ${tenantName} only`}
        </Typography>
        <Box className={classes.messages} role="log" aria-live="polite" aria-busy={isInFlight} aria-label="Assistant conversation">
          {messages.map(message =>
            message.role === 'user' ? (
              <Box key={message.id} className={classes.userMessage}>
                {message.text}
              </Box>
            ) : (
              renderAssistantMessage(message)
            ),
          )}
          <div ref={listEndRef} />
        </Box>
        <form className={classes.composer} onSubmit={handleSubmit}>
          <TextField
            id="merchant-assistant-input"
            label="Ask about this tenant"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            multiline
            maxRows={6}
            fullWidth
            size="small"
            inputProps={{ maxLength: assistantMessageMaxLength }}
          />
          <IconButton type="submit" color="primary" disabled={!canSend} aria-label="Send message">
            <SendIcon />
          </IconButton>
        </form>
      </Drawer>
    </>
  );
};

export default MerchantAssistant;
